import { datadogRum } from '@datadog/browser-rum';
import { sessionURL } from 'logrocket';
import { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { AccountAPI } from 'api/account';
import { RoutePaths } from 'constant';
import { logger } from 'services';
import { fullName } from 'utils/full-name';
import { isNitroEnvironment } from 'utils/is-nitro-env';
import { useAccount } from './use-account';
import { useUser } from './use-user';

declare global {
  interface Window {
    isAppLoaded: boolean;
  }
}

export enum RouteLevelEventTags {
  ProjectConversationPageLoad = 'LOAD: project-coversations:page-load',
  TimeSheetsPageLoad = 'LOAD: timesheets:page-load',
  TasksListPageLoad = 'LOAD: tasks-list:page-load',
  ProjectTasksListPageLoad = 'LOAD: project-tasks-list:page-load',
  TimesheetReportsPageLoad = 'LOAD: timesheets-report:page-load',
  ProjectPlanPageLoad = 'LOAD: project-plan:page-load',
  ProjectListPageLoad = 'LOAD: projects-list:page-load',
  CreateNewProjectModal = 'LOAD: project-list:create-new-project',
}

export enum ClickEventTags {
  ProjectPlanTaskLoad = 'LOAD: project-plan:task-load',
  AllTasksTaskLoad = 'LOAD: all-tasks:task-load',
  CommentPublished = 'ACTION: conversations:post-comment',
  CkMentionPopup = 'ACTION: chat:ck-mention-popup',
  AssigneePickerPopup = 'ACTION: assignee-picker-popup',
  ProjectListFilterChange = 'ACTION: projects-list:filter-change',
  TimesheetProjectPopupOpen = 'ACTION: timesheet:project-popup',
  TimesheetTaskPopupOpen = 'ACTION: timesheet:task-popup',
  TimesheetCalendarDrawerOpen = 'ACTION: timesheet:calendar-drawer-open',
  TimesheetAddPreviousRows = 'ACTION: timesheet:add-last-week-rows',
  TimesheetTimeEntryPopupOpen = 'ACTION: timesheet:time-entry-popup',
  DueDatePickerPopup = 'ACTION: due-date-picker-popup',
  ProjectPlanTaskCreate = 'ACTION: project-plan:task-create',
  ProjectPlanInviteMemberPopup = 'ACTION: project-plan:invite-member-popup',
  ProjectPlanPlaceholderResolutionPopup = 'ACTION: project-plan:placeholder-resolution-popup',
  TaskPaneTimeEntryDrawerOpen = 'ACTION: task-pane:time-entry-drawer',
  ProjectTaskListFilterChange = 'ACTION: projects-tasks-list:filter-change',
  AllTaskFilterChange = 'ACTION: all-tasks:filter-change',
  FieldEdit = 'ACTION: field:value-change',
}

enum AccountGroupingBasedOnSize {
  Smb = 'Smb',
  MidMarket = 'MidMarket',
  Enterprise = 'Enterprise',
}

// add a tag here if you want to log the duration of the events starting from the app load.
// markers for the tags of a particular route will be automatically set if the route changes

export const RouteBasedTagsMap = {
  [RoutePaths.myTimesheets]: [RouteLevelEventTags.TimeSheetsPageLoad],
  [RoutePaths.timesheets]: [RouteLevelEventTags.TimeSheetsPageLoad],
  [RoutePaths.projectConversationDetails]: [
    RouteLevelEventTags.ProjectConversationPageLoad,
  ],
  [RoutePaths.tasks]: [RouteLevelEventTags.TasksListPageLoad],
  [RoutePaths.projectTaskList]: [RouteLevelEventTags.ProjectTasksListPageLoad],
  [RoutePaths.timesheetReports]: [RouteLevelEventTags.TimesheetReportsPageLoad],
  [RoutePaths.projectPlan]: [RouteLevelEventTags.ProjectPlanPageLoad],
  [RoutePaths.projects]: [RouteLevelEventTags.ProjectListPageLoad],
  // make sure to keep create before detail, or else it will not be calculated properly as both have similar route structure
  [RoutePaths.projectCreate]: [RouteLevelEventTags.CreateNewProjectModal],
  [RoutePaths.projectDetail]: [RouteLevelEventTags.ProjectPlanPageLoad],
};

// adding this here because changing route from timeline to plan causes end marker to get set first and then the start marker, which causes discrepancy
// I know it's a hacky solution but what to do
const IgnoreRouteChangeForThese = {
  [RoutePaths.projectPlanTimeline]: RoutePaths.projectPlan,
};

const RedirectedRoutes = {
  [RoutePaths.timesheets]: [
    RoutePaths.myTimesheets,
    RoutePaths.timesheetsApproval,
  ],
  [RoutePaths.projectDetail]: [RoutePaths.projectPlan],
};

export enum LogType {
  AutoLog = 'AUTO',
  CallbackLog = 'MANUAL',
}

const logVoid = (_tagNames: string[]) => {};

export const setMarkerForDataDog = (markerName: string): void => {
  if (window.isAppLoaded) {
    performance.clearMarks(markerName);
    performance.mark(markerName);
  }
};

const clearMarksForDataDog = () => {
  performance.clearMeasures();
  performance.clearMarks();
};

export const useLoadLoggerForDataDog = (
  tagNames: string[],
  logType: LogType = LogType.AutoLog,
): { executeLogger: () => void } => {
  const account = useAccount();
  const { user } = useUser();
  let accountGroup;
  const userCount = account.metaData?.userCount ?? 0;

  if (userCount >= 100) {
    accountGroup = AccountGroupingBasedOnSize.Enterprise;
  } else if (userCount >= 30 && userCount < 100) {
    accountGroup = AccountGroupingBasedOnSize.MidMarket;
  } else {
    accountGroup = AccountGroupingBasedOnSize.Smb;
  }

  const logEntry = useCallback(
    (_tagNames: string[]) => {
      if (!window.isAppLoaded) {
        window.isAppLoaded = true;
      } else {
        _tagNames.forEach((tagName) => {
          try {
            const markerEntry = performance.getEntriesByName(tagName)[0];

            if (markerEntry) {
              const loadMeasure = performance.measure(tagName, tagName);
              const loadTime = Math.floor(loadMeasure.duration);

              const logData = {
                type: tagName,
                userId: user.userId,
                name: fullName(user),
                userType: user.userType,
                account: account.defaultDomainName,
                url: window.location.pathname,
                loadTime,
                logrocketUrl: sessionURL ?? 'No session available',
                rumUrl:
                  datadogRum.getSessionReplayLink() ?? 'No session available',
                build: isNitroEnvironment() ? 'staging' : 'production',
                accountStatus: account.status,
                accountGroup,
              };

              // because of skewness, logging only when load time is less than 1 min
              if (loadTime < 60000) {
                AccountAPI.logAccountMetricsToDataDog({
                  key: 'page_response_time',
                  value: loadTime,
                  tags: {
                    account: account.defaultDomainName,
                    url: window.location.pathname.replaceAll(/\d+/g, '*'),
                    type: tagName,
                  },
                });
                logger.log('page_response_time', logData);
              } else {
                logger.log('page_response_time_skewed', logData);
              }
            }
          } catch (error) {
            logger.error('ERROR_LOGGING_PERFORMANCE_METRICS', { error });
          } finally {
            clearMarksForDataDog();
          }
        });
      }
    },
    [account.defaultDomainName, account.status, accountGroup, user],
  );

  useDeepCompareEffect(() => {
    if (logType === LogType.AutoLog) {
      logEntry(tagNames);
    }
  }, [account.defaultDomainName, logEntry, logType, tagNames]);

  const loggerFunction = logType === LogType.CallbackLog ? logEntry : logVoid;

  return {
    executeLogger: () => loggerFunction(tagNames),
  };
};

const usePrevLocation = (location) => {
  const prevLocRef = useRef(location);

  useEffect(() => {
    prevLocRef.current = location;
  }, [location]);

  return prevLocRef.current;
};

export const useRouteLoadEventInitialiser = () => {
  const location = useLocation();

  const prevLoc = usePrevLocation(location);

  useEffect(() => {
    const ignoredRoute = Object.keys(IgnoreRouteChangeForThese).find(
      (route) => {
        return !!matchPath(prevLoc.pathname, {
          path: route,
          exact: true,
        });
      },
    );

    const redirectedRoute = Object.keys(RedirectedRoutes).find((route) => {
      return !!matchPath(prevLoc.pathname, {
        path: route,
        exact: true,
      });
    });
    const selectedRoute = Object.keys(RouteBasedTagsMap).find((route) => {
      return !!matchPath(location.pathname, {
        path: route,
        exact: true,
      });
    });

    let ignoreMarker = false;
    let isRedirectedRoute = false;

    if (ignoredRoute) {
      if (IgnoreRouteChangeForThese[ignoredRoute] === selectedRoute) {
        ignoreMarker = true;
      }
    }

    if (redirectedRoute) {
      if (
        RedirectedRoutes[redirectedRoute].find(
          (route) => route === selectedRoute,
        )
      ) {
        isRedirectedRoute = true;
      }
    }

    if (!ignoreMarker && !isRedirectedRoute && selectedRoute) {
      (RouteBasedTagsMap[selectedRoute] ?? []).map((tag) =>
        setMarkerForDataDog(tag),
      );
    } else if (selectedRoute == null && !window.isAppLoaded) {
      window.isAppLoaded = true;
    }

    return () => {
      if (
        !Object.keys(RedirectedRoutes).find((route) => {
          return !!matchPath(location.pathname, {
            path: route,
            exact: true,
          });
        })
      ) {
        clearMarksForDataDog();
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
};
